import React, { useState } from 'react'

import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Stack,
  Theme,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { makeStyles } from '@mui/styles'
import WarningIcon from '@mui/icons-material/Warning'
import { Entity, EventLocation, EventLocationResponse, FetchResponse, Message, Method } from '../../../shared/types'
import { useDispatch, useSelector } from 'react-redux'
import { getEventLocations } from '../../../store/data/data.selectors'
import '../../../shared/assets/fonts/font.css'
import TextInputComponent from '../../../shared/components/TextInputComponent'
import LocationSearch from '../../../shared/components/Location/LocationSearch'
import { LatLng } from 'leaflet'
import LocationMap from '../../../shared/components/Location/LocationMap'
import ButtonComponent from '../../../shared/components/ButtonComponent'
import Env from '../../../shared/config/envConfig'
import fetchData from '../../../fetch/fetchData'
import { getApiVersion } from '../../../shared/config/getApiVersion'
import DeleteIcon from '@mui/icons-material/Delete'
import * as dataActions from '../../../store/data/data.actions'
import MediaUploadComponent from '../../../shared/components/MediaUploadComponent'
import RichTextEditorComponent from '../../../shared/components/RichTextEditorComponent'
import { addCsrfHeaders } from '../../../shared/config/csrf'

const LocationComponent: React.FC = () => {
  const theme = useTheme()
  const dispatch = useDispatch()

  const downMd = useMediaQuery(theme.breakpoints.down('md'))
  const downSm = useMediaQuery(theme.breakpoints.down('sm'))

  const basePath = Env.restBase
  const fetchHeaders = Env.fetchHeaders
  addCsrfHeaders(fetchHeaders)

  const eventLocations: EventLocationResponse | undefined = useSelector(getEventLocations)

  const [showLocationDialog, setShowLocationDialog] = useState<boolean>(false)
  const [showLocationDeleteRequestDialog, setShowLocationDeleteRequestDialog] = useState<boolean>(false)
  const [position, setPosition] = useState<LatLng>(new LatLng(51.0834196, 10.4234469))
  const [selectedLocation, setSelectedLocation] = useState<string | undefined>()
  const [newLocation, setNewLocation] = useState<EventLocation | undefined>()
  const [deleteLocation, setDeleteLocation] = useState<string | undefined>()

  const useStyles = makeStyles((theme: Theme) => ({
    locationContainer: {
      display: 'flex',
      width: '100%',
      flexWrap: 'wrap',
      justifyContent: 'flex-start',
      alignItems: 'flex-start',
    },
    locationBox: {
      margin: '10px',
      width: '200px',
      height: '200px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      filter: 'grayscale(100%)',
      '&:hover': {
        filter: 'grayscale(0%)',
      },
    },
    newLocationBox: {
      '&:hover': {
        backgroundColor: theme.palette.grey['500'],
      },
    },
    locationName: {
      color: '#fff',
      fontWeight: 'bold',
      cursor: 'pointer',
      textAlign: 'center',
    },
    icon: {
      cursor: 'pointer',
      color: '#fff',
      '&:hover': {
        color: theme.palette.primary.main,
      },
    },
    baseBox: {
      position: 'relative',
    },
  }))

  const classes = useStyles()

  const setEventLocations = (data: FetchResponse) => {
    dispatch(dataActions.setEventLocations({ count: data.count, results: data.results }))
  }

  const setMessage = (message: Message) => {
    dispatch(setMessage(message))
  }

  const sortLocation = () => {
    return [...(eventLocations?.results || [])].sort((a, b) =>
      (a.name || '').localeCompare(b.name || ''),
    )
  }

  return (
    <>
      <Paper sx={{ margin: downSm ? '0px' : '32px', padding: downSm ? '8px' : '32px' }}>
        <Typography variant='h1'>Veranstaltungsorte</Typography>
        <Typography variant='subtitle1' />
        <Box className={classes.locationContainer}>
          {
            sortLocation().map((location, index) => {
              const img = new Image()
              let backgroundSize = 'auto 200px'
              if (location.imageUrls && location.imageUrls[0]) {
                img.onload = function () {
                  const height = img.height
                  const width = img.width
                  const ratio = width / height
                  if (ratio < 1) {
                    backgroundSize = '200px auto'
                  }
                }
                img.src = (location.imageUrls && location.imageUrls[0]) || '/images/dummyEvent.jpg'
              }
              return (
                <Box
                  key={index}
                  className={classes.locationBox}
                  sx={{
                    position: 'relative',
                    backgroundImage: `url("${location.imageUrls && location.imageUrls[0] ? location.imageUrls[0] : '/images/dummyEvent.jpg'}")`,
                    backgroundSize: backgroundSize,
                  }}
                >
                  <Typography
                    onClick={() => {
                      setSelectedLocation(location.id)
                      setShowLocationDialog(true)
                      setNewLocation(location)
                      const latLngArray = location.coordinates?.split(',')
                      latLngArray && latLngArray.length === 2 && setPosition(new LatLng(latLngArray[0], latLngArray[1]))
                    }}
                    variant='body1' className={classes.locationName}
                  >{location.name}
                  </Typography>
                  <IconButton
                    onClick={() => {
                      setDeleteLocation(location.id)
                      setShowLocationDeleteRequestDialog(true)
                    }}
                    sx={{ position: 'absolute', top: '5px', right: '5px', zIndex: 100 }}
                  >
                    <DeleteIcon
                      className={classes.icon}
                    />
                  </IconButton>
                </Box>
              )
            },
            )
          }
          <Box
            className={`${classes.locationBox} ${classes.newLocationBox}`}
            sx={{
              position: 'relative',
              backgroundColor: theme.palette.grey.A400,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography
              onClick={() => {
                setSelectedLocation('new')
                setShowLocationDialog(true)
                setNewLocation({ name: '' })
              }}
              variant='body1' className={classes.locationName}
            >Neuen<br />Veranstaltungsort<br />anlegen
            </Typography>
          </Box>
        </Box>
        <Dialog
          open={showLocationDialog} onClose={() => setShowLocationDialog(false)}
          sx={{ '& .MuiDialog-paper': { borderRadius: 0, minWidth: '80%', maxWidth: '80%' } }}
        >
          <Stack sx={{ p: 3 }}>
            <Stack
              direction='row' spacing={3} alignItems='center'
              justifyContent='space-between' className={classes.baseBox}
            >
              <DialogTitle sx={{ fontWeight: 600, p: 0, fontSize: '16px', marginBottom: '20px' }}>
                Veranstaltungsort bearbeiten
              </DialogTitle>
            </Stack>
            <DialogContent sx={{ p: 0, overflow: 'visible' }}>
              <Grid container spacing={1}>
                <Grid item xs={downMd ? 12 : 6}>
                  <TextInputComponent
                    label='Name'
                    value={selectedLocation && eventLocations?.results.find(elem => elem.id === selectedLocation)?.name}
                    required
                    fullWidth
                    onChange={(value?: string | number) => {
                      const newValue: string = value as string || ''
                      newLocation && setNewLocation({
                        ...newLocation,
                        name: newValue,
                      })
                    }}
                  />
                </Grid>
                <Grid item xs={downMd ? 12 : 6}>
                  <TextInputComponent
                    label='Signatur'
                    value={selectedLocation && eventLocations?.results.find(elem => elem.id === selectedLocation)?.signature}
                    required
                    fullWidth
                    onChange={(value?: string | number) => {
                      const newValue: string = value as string || ''
                      newLocation && setNewLocation({
                        ...newLocation,
                        signature: newValue,
                      })
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <RichTextEditorComponent
                    label='Beschreibung'
                    height='330px'
                    tooltip='Bitte eine Beschreibung der Location eingeben.'
                    value={selectedLocation && eventLocations?.results.find(elem => elem.id === selectedLocation)?.description}
                    onChange={(value?: string) => {
                      let newValue: string | undefined = value
                      if (newValue === '' || newValue === ' ') {
                        newValue = undefined
                      }
                      newLocation && setNewLocation({
                        ...newLocation,
                        description: newValue,
                      })
                    }}
                  />
                </Grid>
                <Grid item xs={downMd ? 12 : 6}>
                  <TextInputComponent
                    label='Linktitel'
                    value={selectedLocation && eventLocations?.results.find(elem => elem.id === selectedLocation)?.caption}
                    fullWidth
                    onChange={(value?: string | number) => {
                      const newValue: string = value as string || ''
                      newLocation && setNewLocation({
                        ...newLocation,
                        caption: newValue,
                      })
                    }}
                  />
                </Grid>
                <Grid item xs={downMd ? 12 : 6}>
                  <TextInputComponent
                    label='Linkurl'
                    value={selectedLocation && eventLocations?.results.find(elem => elem.id === selectedLocation)?.linkUrl}
                    fullWidth
                    onChange={(value?: string | number) => {
                      const newValue: string = value as string || ''
                      newLocation && setNewLocation({
                        ...newLocation,
                        linkUrl: newValue,
                      })
                    }}
                  />
                </Grid>
                <Grid item xs={downMd ? 12 : 6}>
                  <LocationSearch
                    destination={selectedLocation && eventLocations?.results.find(elem => elem.id === selectedLocation)?.name}
                    setPosition={(obj) => {
                      setPosition(new LatLng(obj.lat, obj.long))
                      newLocation && setNewLocation({
                        ...newLocation,
                        coordinates: `${obj.lat},${obj.long}`,
                      })
                    }}
                  />
                  <LocationMap mapZoom={12} positions={[position]} />
                </Grid>
                <Grid item xs={downMd ? 12 : 6}>
                  <MediaUploadComponent
                    allowVideoUpload={false}
                    file={undefined}
                    setMediaFile={(file) =>
                      // TODO save file
                      file && newLocation && setNewLocation({
                        ...newLocation,
                        imageUrls: [file],
                      })}
                  />
                </Grid>
              </Grid>
            </DialogContent>
          </Stack>
          <DialogActions sx={{ p: 3 }}>
            <ButtonComponent
              color='primary'
              variant='text'
              text='Verwerfen'
              onClick={() => {
                setShowLocationDialog(false)
              }}
            />
            <ButtonComponent
              color='primary'
              variant='contained'
              text='Speichern'
              onClick={() => {
                if (selectedLocation === 'new') {
                  fetchData(basePath, getApiVersion(), fetchHeaders, Entity.eventLocations, Method.POST, undefined, { location: newLocation }, setEventLocations, setMessage)
                } else {
                  fetchData(basePath, getApiVersion(), fetchHeaders, Entity.eventLocations, Method.PUT, selectedLocation, { location: newLocation }, setEventLocations, setMessage)
                }
                setShowLocationDialog(false)
              }}
            />
          </DialogActions>
        </Dialog>
        <Dialog
          open={showLocationDeleteRequestDialog} onClose={() => setShowLocationDeleteRequestDialog(false)}
          sx={{ '& .MuiDialog-paper': { borderRadius: 0 } }}
        >
          <Stack sx={{ p: 3 }}>
            <Stack
              direction='row' spacing={3} alignItems='center'
              justifyContent='space-between' className={classes.baseBox}
            >
              <DialogTitle
                sx={{
                  fontWeight: 600,
                  p: 0,
                  fontSize: '16px',
                  marginBottom: '20px',
                  display: 'flex',
                  flexWrap: 'nowrap',
                  alignItems: 'center',
                }}
              >
                <WarningIcon sx={{ color: theme.palette.warning.main, marginRight: '5px' }} />
                <Typography variant='body1' sx={{ color: theme.palette.warning.main, marginTop: '16px' }}>Veranstaltungsort
                  löschen
                </Typography>
              </DialogTitle>
            </Stack>
            <DialogContent sx={{ p: 0, overflow: 'visible' }}>
              <Typography variant='body1'>Wollen Sie den Veranstaltungsort wirklich löschen? Diese Aktion kann nicht
                rückgängig gemacht werden!
              </Typography>
            </DialogContent>
          </Stack>
          <DialogActions sx={{ p: 3 }}>
            <ButtonComponent
              color='primary'
              variant='text'
              text='Abrechen'
              onClick={() => {
                setShowLocationDeleteRequestDialog(false)
              }}
            />
            <ButtonComponent
              color='primary'
              variant='contained'
              text='Löschen'
              onClick={() => {
                if (deleteLocation) {
                  fetchData(basePath, getApiVersion(), fetchHeaders, Entity.eventLocations, Method.DELETE, deleteLocation, undefined, setEventLocations, setMessage)
                }
                setShowLocationDeleteRequestDialog(false)
              }}
            />
          </DialogActions>
        </Dialog>
      </Paper>
    </>
  )
}

export default LocationComponent
